import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi } from '../../api/adminApi';
import { ComponentCode, ComponentName } from '@padcanvas/shared';
import { LOG_LEVEL_OPTIONS } from '@shared-ui/logging';
import { DataTable, SearchFilterBar, TruncatedValue, type ColumnDef, type FilterDef } from '@shared-ui/components';
import './Logs.css';

type LogRow = {
  id: string;
  requestId: string;
  level: string;
  component: string;
  operation?: string;
  message: string;
  data?: Record<string, unknown>;
  error?: { code: number; word: string; message: string; context?: Record<string, unknown> };
  userId?: string;
  userEmail?: string;
  submissionId?: string;
  duration?: number;
  source?: string;
  timestamp: string;
};

const ALL_COMPONENT_CODES = Object.values(ComponentCode).filter((v): v is ComponentCode => typeof v === 'number');
const COMPONENTS = ALL_COMPONENT_CODES
  .filter(code => code < 60)
  .map(code => ({ code, name: ComponentName[code] }));

const LOG_SOURCE_OPTIONS = [
  { value: 'backend', label: 'Backend' },
  { value: 'web-student', label: 'Web Student' },
  { value: 'web-teacher', label: 'Web Teacher' },
  { value: 'web-admin', label: 'Web Admin' },
  { value: 'ios', label: 'iOS App' },
  { value: 'android', label: 'Android App' },
];

const PAGE_SIZE = 50;

type LogsFilters = {
  date: string;
  level: string;
  component: string;
  source: string;
  requestId: string;
  submissionId: string;
  userEmail: string;
};

const defaultFilters = (): LogsFilters => ({
  date: new Date().toISOString().substring(0, 10),
  level: '',
  component: '',
  source: '',
  requestId: '',
  submissionId: '',
  userEmail: '',
});

export function Logs() {
  const [appliedFilters, setAppliedFilters] = useState<LogsFilters>(defaultFilters);
  const [logsOffset, setLogsOffset] = useState<number>(0);
  const [shouldFetchLogs, setShouldFetchLogs] = useState<boolean>(false);
  const [isClearingLogs, setIsClearingLogs] = useState(false);

  const filterDefs = useMemo<FilterDef[]>(() => [
    { key: 'date', kind: 'date', title: 'Date' },
    {
      key: 'level',
      kind: 'select',
      placeholder: 'All Levels',
      options: LOG_LEVEL_OPTIONS.map(l => ({ value: l.label, label: l.label })),
    },
    {
      key: 'component',
      kind: 'select',
      placeholder: 'All Components',
      options: COMPONENTS.map(c => ({ value: c.name, label: c.name })),
    },
    {
      key: 'source',
      kind: 'select',
      placeholder: 'All Sources',
      options: LOG_SOURCE_OPTIONS,
    },
    { key: 'requestId', kind: 'text', placeholder: 'Request ID' },
    { key: 'submissionId', kind: 'text', placeholder: 'Submission ID' },
    { key: 'userEmail', kind: 'text', placeholder: 'User email' },
  ], []);

  const logColumns = useMemo<ColumnDef<LogRow>[]>(() => [
    { key: 'time', header: 'Time', className: 'log-time', render: (log) => format(new Date(log.timestamp), 'HH:mm:ss.SSS') },
    { key: 'level', header: 'Level', render: (log) => <span className={`log-level level-${log.level.toLowerCase()}`}>{log.level}</span> },
    { key: 'source', header: 'Source', className: 'log-source', render: (log) => log.source || 'backend' },
    { key: 'component', header: 'Component', className: 'log-component', render: (log) => log.component },
    { key: 'message', header: 'Message', className: 'log-message', render: (log) => log.message },
    { key: 'email', header: 'Email', className: 'log-email', render: (log) => log.userEmail ? <TruncatedValue value={log.userEmail} short={log.userEmail.split('@')[0]} /> : '—' },
    { key: 'requestId', header: 'Request ID', render: (log) => (
      <span onClick={e => e.stopPropagation()}>
        <TruncatedValue value={log.requestId} short={log.requestId.split('-').pop()!} />
      </span>
    ) },
  ], []);

  const { data: logsData, isLoading: logsLoading, refetch: refetchLogs } = useQuery({
    queryKey: ['logs', appliedFilters, logsOffset],
    queryFn: () => adminApi.getLogs({
      date: appliedFilters.date,
      level: appliedFilters.level || undefined,
      component: appliedFilters.component || undefined,
      requestId: appliedFilters.requestId || undefined,
      submissionId: appliedFilters.submissionId || undefined,
      source: appliedFilters.source || undefined,
      userEmail: appliedFilters.userEmail || undefined,
      limit: PAGE_SIZE,
      offset: logsOffset,
    }),
    enabled: shouldFetchLogs,
    staleTime: Infinity,
  });

  const handleFiltersChange = (values: Record<string, string>) => {
    setAppliedFilters(prev => ({ ...prev, ...(values as Partial<LogsFilters>) }));
    setLogsOffset(0);
    setShouldFetchLogs(true);
  };

  const totalPages = logsData
    ? (logsData.hasMore ? Math.floor(logsOffset / PAGE_SIZE) + 2 : Math.floor(logsOffset / PAGE_SIZE) + 1)
    : 0;

  return (
    <div className="settings-section logs-section">
      <div className="section-header">
        <h2>System Logs</h2>
      </div>

      <SearchFilterBar
        filters={filterDefs}
        values={appliedFilters as unknown as Record<string, string>}
        onChange={handleFiltersChange}
        onFetch={() => refetchLogs()}
        loading={logsLoading}
        hideSearch
        trailingActions={
          <button
            className="btn-danger logs-delete-all"
            onClick={async () => {
              if (!window.confirm('Are you sure you want to delete ALL logs? This cannot be undone.')) return;
              setIsClearingLogs(true);
              try {
                const result = await adminApi.clearLogs();
                alert(`Deleted ${result.deleted} logs.`);
                refetchLogs();
              } catch (err) {
                alert(`Failed to clear logs: ${err instanceof Error ? err.message : err}`);
              } finally {
                setIsClearingLogs(false);
              }
            }}
            disabled={isClearingLogs || logsLoading}
          >
            {isClearingLogs ? 'Deleting...' : 'Delete All'}
          </button>
        }
      />

      {logsData && (
        <DataTable<LogRow>
          columns={logColumns}
          data={logsData.logs}
          rowKey={(log) => log.id}
          rowClassName={(log) => `log-row level-${log.level.toLowerCase()}`}
          renderExpandedRow={(log) => (
            <div className="log-details">
              <div className="detail-item"><strong>Request ID:</strong> {log.requestId}</div>
              {log.operation && <div className="detail-item"><strong>Operation:</strong> {log.operation}</div>}
              {log.userEmail && <div className="detail-item"><strong>User Email:</strong> {log.userEmail}</div>}
              {log.userId && <div className="detail-item"><strong>User ID:</strong> {log.userId}</div>}
              {log.submissionId && <div className="detail-item"><strong>Submission ID:</strong> {log.submissionId}</div>}
              {log.duration !== undefined && <div className="detail-item"><strong>Duration:</strong> {log.duration}ms</div>}
              {log.data && Object.keys(log.data).length > 0 && (
                <div className="detail-item">
                  <strong>Data:</strong>
                  <pre>{JSON.stringify(log.data, null, 2)}</pre>
                </div>
              )}
              {log.error && (
                <div className="detail-item error-detail">
                  <strong>Error:</strong>
                  <pre>{JSON.stringify(log.error, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
          pagination={logsData && (logsData.hasMore || logsOffset > 0) ? {
            page: Math.floor(logsOffset / PAGE_SIZE) + 1,
            totalPages,
            total: logsData.total,
            onPageChange: (p) => setLogsOffset((p - 1) * PAGE_SIZE),
          } : undefined}
          compact
          bordered
          emptyMessage="No logs found for the selected filters."
        />
      )}

      {!shouldFetchLogs && (
        <div className="no-logs-message">
          Click "Fetch" to load logs from CosmosDB.
        </div>
      )}

    </div>
  );
}
